// CROCUS API utilities
const API_BASE = 'https://data.crocus-micronet.org';

/**
 * Fetch metadata from the API
 */
export async function getMetadata() {
  const response = await fetch(`${API_BASE}/api/v1/metadata`);
  if (!response.ok) {
    throw new Error(`Metadata fetch failed: ${response.statusText}`);
  }
  return await response.json();
}

/**
 * Query sensor data from the API
 * @param {Object} params - Query parameters
 * @param {string} params.start - Start time (e.g., "-7d" or ISO date)
 * @param {string} params.end - End time (optional)
 * @param {string} params.timezone - Timezone (default: "UTC")
 * @param {number} params.tail - Limit results to most recent N records
 * @param {Object} params.filter - Filter object
 * @returns {Promise<Array>} Array of data records
 */
export async function queryData(params) {
  const response = await fetch(`${API_BASE}/api/v1/query`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });
  
  if (!response.ok) {
    throw new Error(`Query failed: ${response.statusText}`);
  }
  
  // Parse newline-delimited JSON
  const text = await response.text();
  const lines = text.trim().split('\n').filter(line => line);
  return lines.map(line => JSON.parse(line));
}

/**
 * Build location data using static device information
 * Optimized with single API call using battery_voltage
 */
export async function getLocationsData() {
  // Import static location data
  const { locationsData } = await import('./constants');
  
  // Clone the static data to avoid mutations
  const locations = JSON.parse(JSON.stringify(locationsData));
  
  // Single optimized API call using battery_voltage with tail=1 for fastest query
  try {
    const latestData = await queryData({
      start: "-30d",
      tail: 1,
      timezone: "UTC",
      filter: {
        name: "battery_voltage"
      }
    });
    
    // Also fetch WXT/AQT data (they don't have battery_voltage)
    let wxtAqtData = [];
    try {
      const wxtData = await queryData({
        start: "-30d",
        tail: 1,
        timezone: "UTC",
        filter: {
          name: "wxt.env.temp"
        }
      });
      const aqtData = await queryData({
        start: "-30d",
        tail: 1,
        timezone: "UTC",
        filter: {
          name: "aqt.env.temp"
        }
      });
      wxtAqtData = [...wxtData, ...aqtData];
    } catch (err) {
      console.error("Error fetching WXT/AQT data:", err);
    }
    
    // Group data by location and device
    const dataByLocationDevice = {};
    [...latestData, ...wxtAqtData].forEach(record => {
      const loc = record.meta.location;
      const deviceId = record.meta.DeviceID;
      const key = `${loc}:${deviceId}`;
      
      if (!dataByLocationDevice[key] || 
          new Date(record.timestamp) > new Date(dataByLocationDevice[key].timestamp)) {
        dataByLocationDevice[key] = record;
      }
    });
    
    // Process each location
    for (const location of locations) {
      let mostRecentTimestamp = 0;
      let onlineCount = 0;
      let totalDevices = location.devices.length;
      
      for (const device of location.devices) {
        const key = `${location.name}:${device.serialNumber}`;
        const deviceData = dataByLocationDevice[key];
        
        const now = Date.now();
        let lastSeen = 0;
        
        if (deviceData) {
          const timestampUTC = deviceData.timestamp.endsWith('Z') 
            ? deviceData.timestamp 
            : deviceData.timestamp + 'Z';
          lastSeen = new Date(timestampUTC).getTime();
          device.lastSeen = deviceData.timestamp;
          device.battery_voltage = deviceData.value;
        } else {
          device.lastSeen = null;
          device.battery_voltage = 3.7;
        }
        
        const timeSinceLastSeen = now - lastSeen;
        
        // Track most recent timestamp for location
        if (lastSeen > mostRecentTimestamp) {
          mostRecentTimestamp = lastSeen;
        }
        
        // Determine status (reporting if seen within last hour)
        if (timeSinceLastSeen < 3600000) {
          device.status = "ok";
          onlineCount++;
        } else if (lastSeen > 0) {
          device.status = "failed";
        } else {
          device.status = "no-data";
        }
      }
      
      // Calculate overall location status
      if (onlineCount === totalDevices) {
        location.status = "ok";
      } else if (onlineCount > 0) {
        location.status = "degraded";
      } else {
        location.status = "offline";
      }
      
      // Store most recent timestamp for location
      location.lastSeen = mostRecentTimestamp > 0 ? new Date(mostRecentTimestamp).toISOString() : null;
    }
  } catch (error) {
    console.error("Error fetching location data:", error);
    // Set default values on error
    locations.forEach(location => {
      location.status = "no-data";
      location.lastSeen = null;
      location.devices.forEach(device => {
        device.status = "no-data";
        device.battery_voltage = 3.7;
        device.lastSeen = null;
      });
    });
  }
  
  return locations;
}

/**
 * Generate curl command for query
 */
export function getCurlCommand(params) {
  return `curl -X POST ${API_BASE}/api/v1/query \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(params, null, 2)}'`;
}

/**
 * Generate Python code snippet for query
 */
export function getPythonSnippet(params) {
  const { filter = {} } = params;
  
  // Build filter object
  const filterEntries = Object.entries(filter).map(([key, value]) => {
    return `        "${key}": "${value}"`;
  }).join(',\n');
  
  return `import requests
import json

# CROCUS API query
url = "${API_BASE}/api/v1/query"
params = {
    "start": "${params.start || '-30d'}",
    "timezone": "${params.timezone || 'UTC'}",
    "filter": {
${filterEntries}
    }
}

response = requests.post(url, json=params)
data = []

# Parse newline-delimited JSON
for line in response.text.strip().split('\\n'):
    if line:
        data.append(json.loads(line))
        
print(f"Retrieved {len(data)} records")
print("\\nAll records:")
for record in data:
    print(record)`;
}